import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import axios from 'axios';

var api = axios.create({ withCredentials: true });

const VideoPlayer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const videos = [
    {
      key: 'dashboard.mp4',
      title: 'Dashboard Tutorial',
      description: 'Learn how to navigate and use the dashboard features'
    },
    {
      key: 'events.mp4', 
      title: 'Events Management',
      description: 'How to manage upcoming and all events effectively'
    },
    {
      key: 'subscriptions.mp4',
      title: 'Report Subscriptions',
      description: 'Setting up and managing your report subscriptions'
    },
    {
      key: 'payments.mp4',
      title: 'Payment Preferences',
      description: 'Setting up and managing your payment preferences'
    },
    {
      key: 'tickets.mp4',
      title: 'Ticket Inventory',
      description: 'Adding tickets to your ticket inventory'
    }
  ];

  const loadVideo = async (videoKey) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0AUDIENCE,
        scope: process.env.REACT_APP_AUTH0SCOPE
      });

      // Get presigned URL for streaming
      const response = await api.get(
        `${process.env.REACT_APP_BASEURL}/api/video/presigned/${videoKey}`,
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Use the presigned URL directly for streaming
      setVideoUrl(response.data.url);
      setLoading(false);
      return response.data.url;
    } catch (error) {
      console.error('Error loading video:', error);
      setLoading(false);
      return null;
    }
  };

  const handleVideoSelect = async (video) => {
    setSelectedVideo(video);
    setVideoUrl(null);
    
    // Clean up previous video URL if it's a blob URL
    if (videoUrl && videoUrl.startsWith('blob:')) {
      URL.revokeObjectURL(videoUrl);
    }
    
    // Load new video
    await loadVideo(video.key);
  };

  const handleMobileVideoChange = (event) => {
    const videoKey = event.target.value;
    const video = videos.find(v => v.key === videoKey);
    if (video) {
      handleVideoSelect(video);
    }
  };

  // Clean up video URL when component unmounts
  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Video Selector */}
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Training Video</InputLabel>
            <Select
              value={selectedVideo?.key || ''}
              label="Select Training Video"
              onChange={handleMobileVideoChange}
            >
              {videos.map((video) => (
                <MenuItem key={video.key} value={video.key}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {video.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {video.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', flex: 1, gap: 2 }}>
        {/* Desktop Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Paper sx={{ width: 300, p: 2 }}>
            <List>
              {videos.map((video) => (
                <ListItem key={video.key} disablePadding>
                  <ListItemButton
                    selected={selectedVideo?.key === video.key}
                    onClick={() => handleVideoSelect(video)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    <PlayCircleOutlineIcon sx={{ mr: 2 }} />
                    <ListItemText
                      primary={video.title}
                      secondary={video.description}
                      secondaryTypographyProps={{
                        sx: {
                          color: selectedVideo?.key === video.key ? 'inherit' : 'text.secondary'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Video Player */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedVideo ? (
            <Paper sx={{ p: 2, flex: 1 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '0',
                  paddingBottom: '56.25%', // 16:9 aspect ratio
                  backgroundColor: '#000',
                  borderRadius: 1,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CircularProgress color="primary" />
                  </Box>
                ) : videoUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    autoPlay
                    playsInline // Better mobile experience
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()} // Disable right-click
                    key={selectedVideo.key} // Force re-render when video changes
                  >
                    <source
                      src={videoUrl}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography color="white">
                      Failed to load video. Please try again.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.100',
                borderRadius: 1,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PlayCircleOutlineIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="grey.500">
                  Select a video to start learning
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPlayer;
